import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, LinkIcon } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupInvites, type AdminInvite } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.08 — Taklif havolalari. GET /admin/groups/:id/invites (backend `2bab8ae`).
 * Token QAYTMAYDI — faqat metama'lumot (nom/foydalanish/muddat/holat/muallif).
 */
export function GroupInvites() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminInvite>[]>(
    () => [
      { key: 'name', header: t.col.title, type: 'text', minWidth: 160 },
      {
        key: 'usage_count',
        header: t.invites.usage,
        type: 'text',
        width: 120,
        sortable: false,
        render: (r) => (
          <span className="font-mono tabular">
            {r.usage_count}
            {r.usage_limit ? ` / ${r.usage_limit}` : ' / ∞'}
          </span>
        ),
      },
      { key: 'creator', header: t.invites.creator, type: 'text', width: 150 },
      { key: 'is_primary', header: t.invites.primary, type: 'bool', width: 100 },
      {
        key: 'is_revoked',
        header: t.col.status,
        type: 'enum',
        width: 120,
        accessor: (r) => (r.is_revoked ? 'revoked' : 'active'),
        options: [
          { value: 'active', label: t.status.active, status: 'active' },
          { value: 'revoked', label: t.users.revoked, status: 'deleted' },
        ],
      },
      { key: 'expire_date', header: t.invites.expires, type: 'date', width: 150, hiddenByDefault: true },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupInvites(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.invites.title}
        screenId="A4.08"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminInvite>
        screenId="A4.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <LinkIcon size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
